import React, { useCallback, useEffect, useState } from "react";
import {
  FaCheck,
  FaFingerprint,
  FaPen,
  FaPlus,
  FaShieldAlt,
  FaTimes,
  FaToggleOff,
  FaToggleOn,
  FaTrashAlt,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { isPlatformAdmin } from "../../constants/roles";
import "./WhitelistedIocs.css";
import "./ProtectedDomains.css";

const API_PATH = "/api/admin/protected-domains";

/**
 * Manages the organization's protected domains. At analysis time the homoglyph
 * detector compares each email's sender / reply-to / link hosts against this
 * list and flags look-alikes (rnicrosoft.com vs microsoft.com, micros0ft.com,
 * Cyrillic/IDN twins, near-spelling typosquats) in the verdict's Homoglyphs
 * section. Platform Admin only.
 */
export default function ProtectedDomains() {
  const { token, user } = useAuth();
  const canManage = isPlatformAdmin(user?.role);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [form, setForm] = useState({ value: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Inline edit of an existing entry (domain / label).
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ value: "", notes: "" });
  const [editError, setEditError] = useState("");

  const PAGE_SIZE = 8;
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visibleEntries = entries.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_PATH, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setEntries(json.entries || []);
    } catch (err) {
      setError(err.message || "Failed to load protected domains");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  if (!canManage) {
    return (
      <div className="wl-forbidden">
        <FaShieldAlt />
        <p>This section is only available to platform administrators.</p>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.value.trim()) {
      setFormError("A domain is required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(API_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value: form.value.trim(), notes: form.notes.trim() || null }),
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setEntries((prev) => [json.entry, ...prev]);
      setForm({ value: "", notes: "" });
      setPage(0);
    } catch (err) {
      setFormError(err.message || "Failed to add domain");
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (entry) => {
    setBusyId(entry.id);
    try {
      const res = await fetch(`${API_PATH}/${entry.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !entry.is_active }),
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? json.entry : e)));
    } catch (err) {
      setError(err.message || "Failed to update domain");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (entry) => {
    if (!window.confirm(`Remove protected domain "${entry.value}"? This cannot be undone.`)) return;
    setBusyId(entry.id);
    try {
      const res = await fetch(`${API_PATH}/${entry.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch (err) {
      setError(err.message || "Failed to delete domain");
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({ value: entry.value, notes: entry.notes || "" });
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError("");
  };

  const saveEdit = async (entry) => {
    setEditError("");
    if (!editForm.value.trim()) {
      setEditError("A domain is required.");
      return;
    }
    setBusyId(entry.id);
    try {
      const res = await fetch(`${API_PATH}/${entry.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value: editForm.value.trim(), notes: editForm.notes.trim() }),
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? json.entry : e)));
      setEditingId(null);
    } catch (err) {
      setEditError(err.message || "Failed to save changes");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="wl-page pd-page">
      <div className="wl-header">
        <h2>Protected Domains</h2>
        <p className="wl-sub">
          Your organization's own and trusted brand domains. During analysis, any sender, reply-to,
          or link domain that visually impersonates one of these — confusable characters
          (<code>rnicrosoft.com</code> for <code>microsoft.com</code>), digit/letter swaps
          (<code>micros0ft.com</code>), Unicode/IDN twins, or near-identical typosquats — is called
          out under the verdict's <strong>Homoglyphs</strong> section and raises the verdict.
        </p>
      </div>

      {/* ── Add form ── */}
      <form className="wl-form" onSubmit={onSubmit}>
        <div className="wl-form-row">
          <label className="wl-form-field wl-field-value">
            <span>Domain</span>
            <input
              type="text"
              placeholder="e.g. microsoft.com or yourcompany.com"
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            />
          </label>
          <label className="wl-form-field wl-field-notes">
            <span>Label (optional)</span>
            <input
              type="text"
              placeholder="e.g. Corporate domain, Microsoft 365"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>
          <div className="wl-form-field wl-field-submit">
            <span aria-hidden="true">&nbsp;</span>
            <button type="submit" className="wl-form-submit" disabled={submitting}>
              <FaPlus />
              <span>{submitting ? "Adding…" : "Add"}</span>
            </button>
          </div>
        </div>
        {formError && <div className="wl-form-error">{formError}</div>}
      </form>

      {error && <div className="wl-error">{error}</div>}

      {/* ── List ── */}
      {loading ? (
        <div className="wl-loading">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="wl-empty">
          <FaFingerprint />
          <p>No protected domains yet. Add your organization's domains above.</p>
        </div>
      ) : (
        <>
          <div className="wl-list">
            {visibleEntries.map((e) =>
              editingId === e.id ? (
                <div key={e.id} className="wl-row wl-row-editing">
                  <span className="wl-type-pill pd-type-pill">
                    <FaFingerprint /> Domain
                  </span>
                  <div className="wl-edit-fields">
                    <input
                      type="text"
                      className="wl-edit-input"
                      placeholder="e.g. microsoft.com or yourcompany.com"
                      value={editForm.value}
                      onChange={(ev) => setEditForm((f) => ({ ...f, value: ev.target.value }))}
                      disabled={busyId === e.id}
                      autoFocus
                    />
                    <input
                      type="text"
                      className="wl-edit-input"
                      placeholder="Label (optional) — e.g. Corporate domain"
                      value={editForm.notes}
                      onChange={(ev) => setEditForm((f) => ({ ...f, notes: ev.target.value }))}
                      disabled={busyId === e.id}
                    />
                    {editError && <div className="wl-form-error wl-edit-error">{editError}</div>}
                  </div>
                  <div className="wl-row-actions">
                    <button
                      type="button"
                      className="wl-save"
                      onClick={() => saveEdit(e)}
                      disabled={busyId === e.id}
                      title="Save changes"
                    >
                      <FaCheck />
                    </button>
                    <button
                      type="button"
                      className="wl-cancel"
                      onClick={cancelEdit}
                      disabled={busyId === e.id}
                      title="Cancel"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ) : (
                <div key={e.id} className={`wl-row ${e.is_active ? "is-active" : "is-inactive"}`}>
                  <span className="wl-type-pill pd-type-pill">
                    <FaFingerprint /> Domain
                  </span>
                  <div className="wl-value-cell">
                    <div className="wl-value">{e.value}</div>
                    {e.notes && <div className="wl-notes">{e.notes}</div>}
                    <div className="wl-meta">
                      Added by {e.created_by || "—"} ·{" "}
                      {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                      {!e.is_active && <span className="wl-inactive-tag"> · INACTIVE</span>}
                    </div>
                  </div>
                  <div className="wl-row-actions">
                    <button
                      type="button"
                      className="wl-edit"
                      onClick={() => startEdit(e)}
                      disabled={busyId === e.id}
                      title="Edit"
                    >
                      <FaPen />
                    </button>
                    <button
                      type="button"
                      className={`wl-toggle ${e.is_active ? "on" : "off"}`}
                      onClick={() => onToggle(e)}
                      disabled={busyId === e.id}
                      title={e.is_active ? "Deactivate" : "Reactivate"}
                    >
                      {e.is_active ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                    <button
                      type="button"
                      className="wl-delete"
                      onClick={() => onDelete(e)}
                      disabled={busyId === e.id}
                      title="Delete"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {totalPages > 1 && (
            <div className="wl-pager">
              <button
                type="button"
                className="wl-pager-btn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
              >
                Prev
              </button>
              <span className="wl-pager-info">
                Page {safePage + 1} of {totalPages} · {entries.length} total
              </span>
              <button
                type="button"
                className="wl-pager-btn"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
